import type { FC, ReactNode } from 'react'

import { Camera, Clapperboard } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MediaPlaceholderProps {
    slotKey: string
    label?: string | null
    labelCms?: string
    ratio?: string | null
    ratioCms?: string
    hint?: string | null
    hintCms?: string
    kind?: 'image' | 'video'
    tone?: 'dark' | 'light'
    compact?: boolean
    className?: string
}

const cornerBase = 'pointer-events-none absolute size-4'

// Art-directed stand-in for a photo or video that has not been supplied yet.
// Shows the media slot name and its brief so the owner knows exactly what belongs here.
export const MediaPlaceholder: FC<MediaPlaceholderProps> = ({
    slotKey,
    label,
    labelCms,
    ratio,
    ratioCms,
    hint,
    hintCms,
    kind = 'image',
    tone = 'dark',
    compact = false,
    className
}): ReactNode => {
    const Icon = kind === 'video' ? Clapperboard : Camera
    const dark = tone === 'dark'
    const cornerColor = dark ? 'border-ash/40' : 'border-smoke/40'

    return (
        <div
            role='img'
            aria-label={`Platzhalter: ${label ?? slotKey}`}
            className={cn(
                'absolute inset-0 flex flex-col justify-between overflow-hidden',
                compact ? 'p-4' : 'p-5 sm:p-6',
                dark ? 'bg-ink-soft text-ash' : 'bg-stone text-smoke',
                className
            )}
        >
            <div
                aria-hidden='true'
                className={cn(
                    'absolute inset-0',
                    dark
                        ? 'bg-[radial-gradient(120%_80%_at_70%_15%,rgba(222,116,72,0.11),transparent_60%)]'
                        : 'bg-[radial-gradient(120%_80%_at_70%_15%,rgba(180,71,31,0.08),transparent_60%)]'
                )}
            />
            <div aria-hidden='true' className='grain-layer' />

            <span aria-hidden='true' className={cn(cornerBase, 'left-3 top-3 border-l border-t', cornerColor)} />
            <span aria-hidden='true' className={cn(cornerBase, 'right-3 top-3 border-r border-t', cornerColor)} />
            <span aria-hidden='true' className={cn(cornerBase, 'bottom-3 left-3 border-b border-l', cornerColor)} />
            <span aria-hidden='true' className={cn(cornerBase, 'bottom-3 right-3 border-b border-r', cornerColor)} />

            <div aria-hidden='true' className='relative flex items-start justify-between gap-3'>
                <span className='type-label break-all'>
                    {slotKey}
                </span>

                <span className={cn('type-label shrink-0 rounded-full border px-2 py-1', dark ? 'border-ember-bright/50 text-ember-bright' : 'border-ember/50 text-ember')}>
                    Platzhalter
                </span>
            </div>

            <div aria-hidden='true' className='relative flex justify-center'>
                <span className={cn('flex items-center justify-center rounded-full border', compact ? 'size-11' : 'size-14', dark ? 'border-ash/30' : 'border-smoke/30')}>
                    <Icon className={compact ? 'size-4' : 'size-5'} strokeWidth={1.5} />
                </span>
            </div>

            <div aria-hidden='true' className='relative flex flex-col gap-1.5'>
                <p className={cn('text-sm font-medium', dark ? 'text-bone/85' : 'text-ink/80')}>
                    {label
                        ? <span data-mdlfy-cms={labelCms}>{label}</span>
                        : <span>{kind === 'video' ? 'Video folgt' : 'Foto folgt'}</span>}

                    {ratio && (
                        <>
                            <span> · </span>
                            <span data-mdlfy-cms={ratioCms}>{ratio}</span>
                        </>
                    )}
                </p>

                {hint && !compact && (
                    <p data-mdlfy-cms={hintCms} className='line-clamp-2 max-w-sm text-xs leading-relaxed'>
                        {hint}
                    </p>
                )}
            </div>
        </div>
    )
}
